/**
 * ONE definition of "is this item out of stock, low, or fine".
 *
 * WHY THIS FILE EXISTS
 * The KPI tile that COUNTS and the list its click OPENS were two separate rules, and they
 * did not agree (found 2026-09-12):
 *
 *   pages/Inventory.js       out of stock when  quantity_on_hand <= 0
 *   services/InventoryService.js  OUT_OF_STOCK when  available === 0,
 *                                 where available = on_hand - reserved
 *
 * An item with 10 on hand and all 10 reserved is therefore NOT out of stock to the tile and
 * IS out of stock to the list. Clicking the tile sets `stockFilter`, which the items tab
 * then applies with the service's rule — so the number on the card and the number of rows
 * you get can never be reconciled by the user.
 *
 * It has not been visible yet: measured live 2026-09-12, 0 of 92 items were fully reserved,
 * so both rules happened to return 4. That is a property of today's data, not of the code —
 * the first job that reserves an item's whole quantity makes the two disagree. (Jerry's
 * 48f32d34 already carries 100 reserved against 1147 on hand.)
 *
 * Same class, and same fix, as utils/customerSegments.js and utils/invoiceAr.js: one owner,
 * imported by both sides, so a future change cannot land on one and miss the other.
 *
 * AVAILABLE, NOT ON-HAND, is the right basis. Stock promised to a job is not stock you can
 * promise to another one, and every consuming screen (the items grid, the restock
 * suggestions, the low-stock chips) already reasons in available terms.
 */

export const STOCK_STATUS = {
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  LOW_STOCK: 'LOW_STOCK',
  IN_STOCK: 'IN_STOCK',
};

/**
 * Used when an item carries no reorder point of its own. Both previous implementations
 * already defaulted to 5 (`item.reorder_point || 5`); it is named here so there is one
 * place to change it rather than two literals to keep in step.
 */
export const DEFAULT_REORDER_POINT = 5;

/**
 * Markup applied to an item's cost when nobody has set one, as a percentage.
 *
 * MIRRORS THE COLUMN DEFAULT — `inventory_items.default_markup_percent` is `25` (verified
 * live 2026-09-18). Named here for the same reason as the reorder point above: it was a
 * bare literal in ItemModal and an unstated assumption in InventoryService.createItem,
 * which derives sell_price from it. A caller that did not happen to send a markup got no
 * derivation at all and the row fell back to `sell_price` 0.00 — the item priced at nothing
 * while every screen showed an estimate based on the 25 the database had quietly supplied.
 */
export const DEFAULT_MARKUP_PERCENT = 25;

/**
 * Resolve an item's reorder threshold. NULL means "not set" and falls back to the default;
 * an explicit 0 means "never flag this item" and is HONOURED, not replaced.
 *
 * Board #36, operator ruling 2026-09-17. This used to be `Number(reorderPoint) || DEFAULT`,
 * and the `||` was deliberate: with a column default of 0 and no NULLs anywhere in the table,
 * a stored 0 genuinely did mean "nobody set one", so substituting 5 was the best available
 * guess. The cost was that a contractor who typed 0 on purpose — meaning stop telling me to
 * reorder this — got the item flagged the moment stock fell to 5, the exact opposite of what
 * they asked for, with no way to express the intent at all.
 *
 * Migration `20260917_reorder_point_unset_is_null.sql` made "not set" representable: the
 * column default is now NULL and the 100 existing 0s were backfilled to NULL, since with a
 * default of 0 none of them COULD have been deliberate. So the ambiguity `||` was working
 * around no longer exists, and `??` is now both correct and safe — every row that used to
 * reach the `|| 5` branch still reaches the `?? 5` branch.
 *
 * Write paths must keep this distinction alive: an empty input saves NULL, a typed 0 saves 0.
 * `ItemModal` used to save an empty field as 0 while `IndustryStandardItemModal` saved it as
 * NULL, so the same blank field produced different rows depending on which modal you opened.
 */
export const resolveReorderPoint = (reorderPoint) => {
  if (reorderPoint === null || reorderPoint === undefined || reorderPoint === '') {
    return DEFAULT_REORDER_POINT;
  }
  const n = Number(reorderPoint);
  return Number.isFinite(n) ? n : DEFAULT_REORDER_POINT;
};

/** Quantity that can still be committed: on hand minus what is already spoken for. */
export const availableQuantity = (onHand, reserved) =>
  Math.max(0, (Number(onHand) || 0) - (Number(reserved) || 0));

/**
 * The status of one item.
 *
 * @param {object}  input
 * @param {number}  input.onHand        total quantity_on_hand across locations
 * @param {number}  input.reserved      total quantity_reserved across locations
 * @param {number} [input.reorderPoint] the item's reorder point; falls back to the default
 */
export const stockStatusOf = ({ onHand = 0, reserved = 0, reorderPoint } = {}) => {
  const available = availableQuantity(onHand, reserved);
  const threshold = resolveReorderPoint(reorderPoint);

  if (available === 0) return STOCK_STATUS.OUT_OF_STOCK;
  if (available <= threshold) return STOCK_STATUS.LOW_STOCK;
  return STOCK_STATUS.IN_STOCK;
};

/**
 * "Do I need to order more of this?" — which is NOT the same question as "what is this item's
 * status", and conflating them is board #22.
 *
 * The three statuses are mutually exclusive, so an item sitting at zero is OUT_OF_STOCK and
 * therefore NOT LOW_STOCK. The Reorder Needed tile counted LOW_STOCK only, under the
 * description "At or below reorder point" — so an item with a reorder point of 10 and nothing
 * on the shelf was excluded from the one list whose whole job is telling you what to buy. The
 * tester created exactly that item and watched the counter stay put.
 *
 * Excluding the items you have NONE of from the reorder list inverts it: those are the most
 * urgent rows, not the least. So Reorder Needed deliberately OVERLAPS Out of Stock, and the
 * tile now says so rather than leaving the reader to work out why 3 + 5 is not 8.
 *
 * Expressed as available <= threshold, which is the tile's stated rule read literally. That is
 * equivalent to "status is not IN_STOCK" — the default threshold is never 0 — but stating the
 * comparison keeps this honest if the status rule ever changes.
 *
 * THE ONE EXCEPTION, board #36 (operator ruling 2026-09-17): a reorder point of 0 means the
 * contractor opted this item OUT of reorder prompts, so it is never listed however empty it is.
 * Without this branch "never flag" would collapse into "flag precisely when empty", because
 * `available <= 0` is true at exactly zero — and zero is the normal resting state for the kind
 * of item somebody opts out (a special-order part they buy per job and never stock).
 *
 * This does NOT undo #22 above. #22 is about an item with a real threshold and nothing on the
 * shelf — reorder point 10, available 0 — which still returns true here. Only a deliberate 0
 * opts out, and before the migration a deliberate 0 could not even be stored.
 *
 * Note the two questions now separate for such an item: it is still OUT_OF_STOCK to
 * `stockStatusOf` (you do have none of it, which is #88's ruling) while not being Reorder
 * Needed (you said not to ask). That is the intended reading of both rulings together.
 */
export const needsReorder = ({ onHand = 0, reserved = 0, reorderPoint } = {}) => {
  const threshold = resolveReorderPoint(reorderPoint);
  if (threshold === 0) return false;
  return availableQuantity(onHand, reserved) <= threshold;
};

/** The same question, for code holding a computed status string rather than the raw numbers. */
export const statusNeedsReorder = (status) =>
  status === STOCK_STATUS.LOW_STOCK || status === STOCK_STATUS.OUT_OF_STOCK;

export const isOutOfStock = (input) => stockStatusOf(input) === STOCK_STATUS.OUT_OF_STOCK;
export const isLowStock = (input) => stockStatusOf(input) === STOCK_STATUS.LOW_STOCK;
export const isHealthyStock = (input) => stockStatusOf(input) === STOCK_STATUS.IN_STOCK;
